import { PhotoMetadataRecord, PhotoRecord } from "@/lib/catalog"
import {
	currentPhotoIdAtom,
	getPhotoMetadata,
	updatePhotoMetadata,
} from "@/store/photo/photos-state"
import { fetchCurrentAdjustmentsFromHistory } from "@/store/store-history"
import { AI } from "@/lib/ai/ai"
import { createPreviewRecord, fetchCurrentPreview, fetchPreviewWithTags } from "@/storage/indexeddb"
import { ProjectItem } from "@/store/project/projects-service"
import { useAtomValue } from "jotai"
import { useEffect, useMemo } from "react"
import { focusAtom } from "jotai-optics"
import { getFileForPhoto } from "@/store/files-state"
import { previewQueue } from "@/store/processing/processing-queue"
import { PreviewLoader } from "@/lib/metadata/metadata-loader"
import {
	previewsAtom,
	resizeAndStoreSmallPreview,
	updatePreviews,
} from "@/store/previews/previews-state"
import { PreviewSize, PreviewTag, getPreviewId } from "@/store/previews/previews-types"
import { projectAtom } from "../project/projects-state"
import { store } from "../store"
import { getEditor } from "@/utils/useEditor"
import { exportImage, type ExportImageOptions } from "@/core/export-image"
import { defaultPreviewOptions } from "./previews-constants"
import { previewImporter } from "../importing/preview-importing"
import { cullingImporter } from "../importing/culling-importing"
import { CullingInputDimension } from "@/lib/ai/utils"
import {
	cullingAutoExposurePreviewUrlsAtom,
	isCullingAutoExposureOnAtom,
} from "../importing/culling-state"
import { JpegEncoder } from "@/lib/jpeg/jpeg-encoder"

export const addExtractPreviewTask = (
	project: ProjectItem,
	photo: PhotoRecord,
	size: PreviewSize,
) => {
	previewQueue.addTask(`${photo.id}/${size}`, {
		purpose: "apply",
		handler: () => fetchOrExtractPreview(project.id, photo, size),
	})
}

export const fetchPreviewDataOOB = async (
	projectId: string,
	photo: PhotoRecord,
	size: PreviewSize,
) => {
	const existingPreview = await fetchCurrentPreview({
		projectId: projectId,
		photo: photo,
		size: size,
	})

	return existingPreview?.data
}

export const fetchOriginalPreviewDataOOB = async (
	projectId: string,
	photo: PhotoRecord,
	size: PreviewSize,
) => {
	const existingPreview = await fetchPreviewWithTags({
		projectId: projectId,
		photoId: photo.id,
		size: size,
		tags: ["original", "rendered"],
	})

	return existingPreview?.data
}

export const fetchOrExtractPreview = async (
	projectId: string,
	photo: PhotoRecord,
	size: PreviewSize,
) => {
	const existingPreview = await fetchCurrentPreview({
		projectId: projectId,
		photo: photo,
		size: size,
	})

	if (existingPreview) {
		//Already exists
		void updatePreviews({ previewsInfo: [{ ...existingPreview, size }], skipStoreToDB: true })
		return existingPreview
	}

	if (size === PreviewSize.small) {
		//Try fetching medium preview
		const mediumPreview = await fetchCurrentPreview({
			projectId: projectId,
			photo: photo,
			size: PreviewSize.medium,
		})
		if (mediumPreview) {
			//Resize medium preview to small
			void resizeAndStoreSmallPreview({ ...mediumPreview })
			return
		}
	}

	const file = getFileForPhoto(photo)
	if (!file) {
		const currentProject = store.get(projectAtom)

		if (currentProject) {
			console.warn("Warning: file is misplaced in the source directory and cannot be found.")
			return
		} else {
			// Project was closed, file access is impossible anymore.
			return
		}
	}

	let previewData: Blob | undefined | null
	let previewSize: PreviewSize = size
	let tags: PreviewTag[]
	if (!photo.adjustmentsDigest) {
		// This photo has no adjustments.
		// Grab preview from metadata (not getting rendered preview because AI editor could be in use)
		previewData = await PreviewLoader.getImage(file, size, CullingInputDimension)
		tags = ["extracted", "original"]
		if (!previewData) {
			let metadata = getPhotoMetadata(photo.id)
			if (!metadata) {
				console.warn(
					"Extract preview: metadata is not available yet, extract now.",
					photo.id,
				)
				metadata = (await PreviewLoader.getMetadata(file)).record
			}
			previewData = await previewImporter.extractRenderedPreviewOOB({
				photo,
				metadata,
			})
		}
	} else {
		// Photo has adjustments.
		// Render preview from original photo
		// Render previews are medium size
		previewData = await renderPreview(file, photo)
		tags = ["edited", "rendered"]
		previewSize = PreviewSize.medium
	}

	if (previewData) {
		// store in atom and db for future access
		const previewInfo = {
			projectId: projectId,
			photoId: photo.id,
			data: previewData,
			size: previewSize,
			digest: photo.adjustmentsDigest ?? undefined,
			tags,
		}
		updatePreviews({ previewsInfo: [previewInfo] })
		resizeAndStoreSmallPreview(previewInfo)
		return createPreviewRecord(previewInfo)
	}
}

export const fetchOrExtractRenderedOriginalPreview = async (props: {
	projectId: string
	photo: PhotoRecord
	metadata: PhotoMetadataRecord
	signal?: AbortSignal
}) => {
	const { photo, metadata, projectId, signal } = props
	signal?.throwIfAborted()

	const tags: PreviewTag[] = ["rendered", "original"]
	const info = {
		photoId: photo.id,
		size: PreviewSize.medium,
		projectId,
		tags,
	}

	const existingRenderedPreview = await fetchPreviewWithTags(info)
	signal?.throwIfAborted()
	if (existingRenderedPreview) {
		//store to atom only since its already in db
		void updatePreviews({
			previewsInfo: [
				{
					...info,
					data: existingRenderedPreview.data,
				},
			],
			skipStoreToDB: true,
		})

		return existingRenderedPreview.data
	}

	const file = getFileForPhoto(photo)

	if (!file) {
		return
	}

	let renderMetadata = metadata
	if (!renderMetadata.isComplete) {
		const currentMetadata = getPhotoMetadata(photo.id)
		if (currentMetadata?.isComplete) {
			renderMetadata = currentMetadata
		} else {
			try {
				renderMetadata = {
					...(await PreviewLoader.getMetadata(file)).record,
					photoId: photo.id,
				}
				signal?.throwIfAborted()
				updatePhotoMetadata(photo.id, renderMetadata)
			} catch {
				renderMetadata = currentMetadata ?? renderMetadata
			}
		}
	}
	signal?.throwIfAborted()

	let preview: Blob | null
	let fullSize: [number, number]
	await AI.lockEditor()
	try {
		signal?.throwIfAborted()
		await AI.loadImage(file, { metadata: renderMetadata })
		// Culling may finish while waiting for the editor or loading the image.
		const needBiggerSizeForCulling = !cullingImporter.hasCullingInfo(photo.id)
		preview = await AI.generatePreview({
			...defaultPreviewOptions,
			colorSpace: "display-p3",
			resizeWidth: needBiggerSizeForCulling
				? CullingInputDimension
				: (defaultPreviewOptions.resizeWidth ?? 1024),
			resizeHeight: needBiggerSizeForCulling
				? CullingInputDimension
				: (defaultPreviewOptions.resizeHeight ?? 1024),
		})
		fullSize = await AI.getLoadedImageSize()
	} finally {
		await AI.unlockEditor()
	}
	signal?.throwIfAborted()

	if (preview) {
		const mediumPreviewInfo = {
			...info,
			data: preview,
			size: PreviewSize.medium,
		}
		void updatePreviews({ previewsInfo: [mediumPreviewInfo] })
		void resizeAndStoreSmallPreview(mediumPreviewInfo, fullSize)
	}

	return preview ?? undefined
}

export const renderPreview = async (
	file: File,
	photo: PhotoRecord,
	options: Partial<Pick<ExportImageOptions, "resizeWidth" | "resizeHeight" | "quality">> = {},
) => {
	const metadata = getPhotoMetadata(photo.id)
	const mainEditor = getEditor()

	if (mainEditor.photo.id === photo.id) {
		// Use foreground editor if this file is already loaded
		return await exportImage(mainEditor, {
			...defaultPreviewOptions,
			...options,
			jpegEncoder: JpegEncoder,
		})
	}

	let preview: Blob | null

	try {
		await AI.lockEditor()

		const adjustments = await fetchCurrentAdjustmentsFromHistory(photo)
		await AI.loadImage(file, { metadata })
		preview = await AI.generatePreview({
			...options,
			adjustments,
			colorSpace: "display-p3",
		})
	} finally {
		AI.unlockEditor()
	}

	return preview
}

export const useThumbnailPreview = ({
	photo,
	project,
	previewSize,
}: {
	photo: PhotoRecord
	project: ProjectItem
	previewSize: PreviewSize
}) => {
	const previewId = getPreviewId({
		photoId: photo.id,
		projectId: project.id,
		size: previewSize,
		digest: photo.adjustmentsDigest ?? undefined,
	})

	const originalPreviewId = getPreviewId({
		photoId: photo.id,
		projectId: project.id,
		size: previewSize,
	})
	const previewAtom = useMemo(
		() => focusAtom(previewsAtom, (optic) => optic.prop(previewId)),
		[previewId],
	)

	const originalPreviewAtom = useMemo(
		() => focusAtom(previewsAtom, (optic) => optic.prop(originalPreviewId)),
		[originalPreviewId],
	)
	const currentPhotoId = useAtomValue(currentPhotoIdAtom)
	const allUrls = useAtomValue(cullingAutoExposurePreviewUrlsAtom)
	const isAutoExposureOn = useAtomValue(isCullingAutoExposureOnAtom)

	let autoExposurePreviewUrl: string | undefined = undefined

	if (isAutoExposureOn && currentPhotoId === photo.id) {
		autoExposurePreviewUrl = allUrls[photo.id]
	}

	const currentPreview = useAtomValue(previewAtom)
	const originalPreview = useAtomValue(originalPreviewAtom)

	useEffect(() => {
		const file = getFileForPhoto(photo)
		const needsUpdate = !currentPreview || currentPreview.digest !== photo.adjustmentsDigest
		if (!needsUpdate || !project || !file) {
			return
		}

		addExtractPreviewTask(project, photo, previewSize)

		return () => {
			previewQueue.removeTask(`${photo.id}/${previewSize}`)
			void PreviewLoader.cancelGetImage(file)
		}
	}, [previewId, photo, project, currentPreview, previewSize])

	return {
		url: autoExposurePreviewUrl ?? currentPreview?.url ?? originalPreview?.url,
		hasRenderedOriginalPreview:
			originalPreview?.tags.includes("rendered") === true &&
			originalPreview.tags.includes("original"),
	}
}
